import React, { Fragment } from 'react';
import { Grid, Typography } from '@material-ui/core';

import { SectionCard, DetailsList, LoadingSpinner } from '../';
import { GQLOperationBuilder } from '../../operations/GQLOperationBuilder';
import { GQLPlatformMerchantOrder } from '@jewelutions/core-crm-server-api-client-domain';
import PriceLabel from '../PriceLabel';
import { GQLPlatformMerchantOrderHelper } from '../../operations/helpers/GQLPlatformMerchantOrderHelper';
import EngravingList from '../EngravingList';

interface IProps {
  order: GQLPlatformMerchantOrder;
}

const PlatformMerchantOrderItemListDetails: React.FC<IProps> = ({ order }: IProps) => {
  const orderAssets = GQLPlatformMerchantOrderHelper.getOrderAssets(order);

  const { data: piecesData, loading: piecesLoading } = GQLOperationBuilder.piece.getPieces({
    pieceIds: orderAssets.pieceIds
  });

  const { data: accessoriesData, loading: accessoriesLoading } = GQLOperationBuilder.accessory.getAccessories(
    {
      accessoryIds: orderAssets.accessoryIds
    }
  );

  if (piecesLoading || accessoriesLoading) return <LoadingSpinner />;

  const accessories = GQLOperationBuilder.accessory.accessoryAssetArrayFromResponse(accessoriesData);
  const pieces = GQLOperationBuilder.piece.pieceAssetArrayFromResponse(piecesData);

  return (
    <Fragment>
      <Grid item style={{ marginTop: '8px' }}>
        <SectionCard title="Order Line Details">
          {order.items.map((orderLine, index) => {
            const pieceOrderItem = orderLine.pieceOrderItem!;
            const accessoryOrderItem = orderLine.accessoryOrderItem!;
            const currentPiece = pieces.find(p => p.id === pieceOrderItem.pieceReference.pieceId)!.piece;
            const currentPieceModel = currentPiece.models.find(
              pm => pm.id === pieceOrderItem.pieceReference.pieceModelId
            )!;
            return (
              <Grid style={{ marginTop: '8px' }}>
                <SectionCard title={`Order Line ${index}`} isOutlined>
                  <Typography variant="h5">{`Piece`}</Typography>
                  <DetailsList
                    list={[
                      {
                        primary: 'Name',
                        secondary: currentPiece.title.en
                      },
                      { primary: 'Quantity', secondary: pieceOrderItem.quantity },
                      {
                        primary: `Piece Configuration(Size, Material, Plating)`,
                        secondary: `${pieceOrderItem.pieceReference.pieceConfiguration.sizeType}, ${pieceOrderItem.pieceReference.pieceConfiguration.materialType}, ${pieceOrderItem.pieceReference.pieceConfiguration.platingType}, ${pieceOrderItem.pieceReference.pieceConfiguration.stonesPlacementType}`
                      },
                      {
                        primary: 'Engravings',
                        secondary: (
                          <EngravingList
                            editableAreas={currentPieceModel.editableAreas}
                            engravings={pieceOrderItem.engravings!}
                          />
                        )
                      },
                      {
                        primary: 'Platform price',
                        secondary: <PriceLabel price={pieceOrderItem.platformPrice} />
                      },
                      {
                        primary: 'Retail price',
                        secondary: <PriceLabel price={pieceOrderItem.merchantRetailPrice} />
                      }
                    ]}
                  />
                  <Typography variant="h5">{`Accessory`}</Typography>
                  <DetailsList
                    list={[
                      {
                        primary: 'Name',
                        secondary: accessories.find(
                          a => a.id === accessoryOrderItem.accessoryReference.accessoryId
                        )?.accessory.title.en
                      },
                      { primary: 'Quantity', secondary: accessoryOrderItem.quantity },
                      {
                        primary: `Accessory Configuration(Size, Material, Plating)`,
                        secondary: `${accessoryOrderItem.accessoryReference.accessoryConfiguration.sizeType}, ${accessoryOrderItem.accessoryReference.accessoryConfiguration.materialType}, ${accessoryOrderItem.accessoryReference.accessoryConfiguration.platingType},`
                      },
                      {
                        primary: 'Platform price',
                        secondary: <PriceLabel price={accessoryOrderItem.platformPrice} />
                      },
                      {
                        primary: 'Retail price',
                        secondary: <PriceLabel price={accessoryOrderItem.merchantRetailPrice} />
                      }
                    ]}
                  />
                </SectionCard>
              </Grid>
            );
          })}
        </SectionCard>
      </Grid>
    </Fragment>
  );
};

export default PlatformMerchantOrderItemListDetails;
